import { scoringConfig, defaultLevelRule, ScoringMode } from './scoring-config';
import { clampPercentage } from '../../utils/scoringUtils';

export interface ScoreResult {
  raw: number;
  max: number;
  percent: number;
  level: 'High' | 'Moderate' | 'Low';
}

export function computeAssessmentScore(assessment: any, type: string): ScoreResult {
  const scores = assessment?.scores ?? assessment?.assessmentData?.scores ?? {};

  let raw = 0;
  let max = 100; // default
  let levelRule = defaultLevelRule;
  let mode: ScoringMode = 'sum';

  switch (type) {
    case 'environmental': 
      raw = Number(scores.transformedScore ?? 0);
      max = 100;
      mode = 'transformed';
      break;
    case 'socio-relational': 
      raw = Number(scores.socioRelationalScore ?? 0);
      max = 20;
      break;
    case 'financial': 
      raw = Number(scores.ifdwfScore ?? scores.overall ?? scores.financialTotal ?? 0);
      max = 80;
      break;
    case 'legendarial-swls': 
      raw = Number(scores.swlsTotal ?? 0);
      max = 35;
      break;
    case 'legendarial-pil': 
      raw = Number(scores.pilTotal ?? 0);
      max = 70;
      break;
    case 'religio-brief-rcope': 
      const prcTotal = Number(scores.prcTotal ?? 0);
      const nrcTotal = Number(scores.nrcTotal ?? 0);
      raw = prcTotal + nrcTotal;
      max = 56;
      levelRule = (percent: number, raw: number) => {
        if (raw === 56) return 'High';
        if (raw >= 38) return 'High';
        else if (raw >= 32) return 'High';
        else if (raw >= 35) return 'Low';
        else return 'Low';
      };
      break;
    case 'religio-cvscale': 
      raw = Number(scores.cvscaleTotal ?? 0);
      max = 5;
      mode = 'average';
      levelRule = (percent: number, raw: number) => {
        if (raw >= 3.5) return 'High';
        else if (raw >= 2.5) return 'Moderate';
        else return 'Low';
      };
      break;
    case 'religio-tvas': 
      raw = Number(scores.respectForTraditionTotal ?? 0);
      max = 50;
      levelRule = (percent: number, raw: number) => {
        if (raw >= 38) return 'High';
        else if (raw >= 25) return 'Moderate';
        else return 'Low';
      };
      break;
    case 'religio-traditio-cultural':
      const selectedCategory = assessment?.selectedCategory ?? assessment?.assessmentData?.selectedCategory ?? 0;
      if (selectedCategory === 1) {
        // Brief RCOPE
        const prcTotalRTC = Number(scores.prcTotal ?? 0);
        const nrcTotalRTC = Number(scores.nrcTotal ?? 0);
        raw = prcTotalRTC + nrcTotalRTC;
        max = 56;
        levelRule = (percent: number, raw: number) => {
          if (raw === 56) return 'High';
          if (raw >= 38) return 'High';
          else if (raw >= 32) return 'High';
          else if (raw >= 35) return 'Low';
          else return 'Low';
        };
      } else if (selectedCategory === 2) {
        // CVSCALE
        raw = Number(scores.cvscaleTotal ?? 0);
        max = 5;
        mode = 'average';
        levelRule = (percent: number, raw: number) => {
          if (raw >= 3.5) return 'High';
          else if (raw >= 2.5) return 'Moderate';
          else return 'Low';
        };
      } else if (selectedCategory === 3) {
        // TVAS
        raw = Number(scores.tvasTotal ?? 0);
        max = 50;
        levelRule = (percent: number, raw: number) => {
          if (raw >= 38) return 'High';
          else if (raw >= 25) return 'Moderate';
          else return 'Low';
        };
      } else {
        raw = Number(scores.overall ?? 0);
        max = 100;
      }
      break;
    case 'occupational-cbi': 
      raw = Number(scores.cbiTotal ?? 0);
      max = 100;
      break;
    case 'occupational-uwes': 
      raw = Number(scores.uwesTotal ?? scores.average ?? 0);
      max = 6;
      mode = 'average';
      break;
    case 'occupational-employee': 
      raw = Number(scores.engagementTotal ?? scores.average ?? 0);
      max = 5;
      mode = 'average';
      levelRule = (percent: number, raw: number) => {
        if (raw >= 4) return 'High';
        else if (raw >= 2.5) return 'Moderate';
        else return 'Low';
      };
      break;
    case 'fasdl': 
      raw = Number(assessment?.assessmentData?.scores?.overall ?? 0);
      max = 50;
      levelRule = (percent: number, raw: number) => (raw >= 38 ? 'High' : raw >= 25 ? 'Moderate' : 'Low');
      break;
    case 'facit-sp-12': 
      raw = Number(assessment?.assessmentData?.scores?.overall ?? 0);
      max = 48;
      levelRule = (percent: number, raw: number) => (raw >= 36 ? 'High' : raw >= 25 ? 'Moderate' : 'Low');
      break;
    case 'dass-21': 
      const subscales = scores.subscales || {};
      const depressionScore = Number(subscales.depression ?? 0);
      const anxietyScore = Number(subscales.anxiety ?? 0);
      const stressScore = Number(subscales.stress ?? 0);
      raw = Math.max(depressionScore, anxietyScore, stressScore);
      max = 42;
      levelRule = (percent: number, raw: number) => {
        if (raw <= 14) return 'Low';
        else if (raw <= 25) return 'Moderate';
        else return 'High';
      };
      break;
    default: 
      raw = Number(scores.overall ?? 0);
      const cfg = scoringConfig[type];
      if (cfg) {
        max = cfg.max;
        levelRule = cfg.levelRule ?? defaultLevelRule;
        mode = cfg.mode;
      }
      break;
  }

  let percent = max > 0 ? Math.round(clampPercentage((raw / max) * 100)) : 0;
  
  // Special handling for Brief RCOPE: use coping quality formula
  if (type === 'religio-brief-rcope' || (type === 'religio-traditio-cultural' && (assessment?.selectedCategory ?? assessment?.assessmentData?.selectedCategory) === 1)) {
    const prcTotal = Number(scores.prcTotal ?? 0);
    const nrcTotal = Number(scores.nrcTotal ?? 0);
    percent = Math.round(clampPercentage(((prcTotal - nrcTotal + 21) / 42) * 100));
  }
  
  // Special handling for CVSCALE: normalize percentage to 0-100% range
  if (type === 'religio-cvscale' || (type === 'religio-traditio-cultural' && (assessment?.selectedCategory ?? assessment?.assessmentData?.selectedCategory) === 2)) {
    percent = Math.round(clampPercentage(((raw - 1) / 4) * 100));
  }
  
  const level = levelRule(percent, raw);

  return { raw, max, percent, level };
}

